import { Parameter, ParameterGroup, ParameterType } from 'oas';
import {
    PARAMETER_NAME_TRANSLATION_PREFIX,
    REPORTING_PARAMETER_GROUP_NAME,
    REPORTING_PARAMETER_GROUP_NEW_NAME,
    VECTOR_CORE_TRANSLATION_PREFIX,
} from 'shared';

export interface TfParameter extends Parameter {
    translated_name?: string;
}

export interface TfReportingParameterGroup {
    name: string;
    parameters: TfParameter[];
}

const DEFAULT_SLUGS = [
    'accel&decel_efforts',
    'accel&decel_efforts_per_minute',
    'hr_exertion',
    'red_zone',
    'energy',
    'high_metabolic_load_distance',
    'sprint_efforts',
    'sprint_distance_per_minute',
    'high_speed_distance',
    'high_speed_efforts',
    'high_speed_distance_per_minute',
];

const REQUIRES_VECTOR_CORE_RENAME = [
    'velocity_band1_average_distance_session',
    'velocity_band2_average_distance_session',
    'velocity_band3_average_distance_session',
    'velocity_band4_average_distance_session',
    'velocity_band5_average_distance_session',
    'velocity_band6_average_distance_session',
];

const normaliseParameterMetadata = <T extends Parameter>(param: T): T => ({
    ...param,
    aggregation: param.aggregation?.toLowerCase(),
    group_by: param.group_by?.toLowerCase(),
});

const getNewGroup = (parameterSlug: string): string => {
    switch (parameterSlug) {
        case 'accel&decel_efforts':
        case 'accel&decel_efforts_per_minute':
            return 'Acceleration (Gen 2)';
        case 'hr_exertion':
        case 'red_zone':
            return 'Heart Rate';
        case 'high_metabolic_load_distance':
        case 'energy':
            return 'Metabolic Power';
        case 'sprint_efforts':
        case 'sprint_distance_per_minute':
        case 'high_speed_distance':
        case 'high_speed_efforts':
        case 'high_speed_distance_per_minute':
            return 'Velocity';
        default:
            return 'Custom';
    }
};

export const transformParameterGroups = (
    groups: ParameterGroup[],
    types: ParameterType[],
): TfReportingParameterGroup[] => {
    const parameterTypeMap: { [key: string]: ParameterType } = {};
    for (const type of types) {
        parameterTypeMap[type.id || ''] = type;
    }

    const groupMap: { [key: string]: TfReportingParameterGroup } = {};

    for (const slug of DEFAULT_SLUGS) {
        const groupName = getNewGroup(slug);
        if (!groupMap[groupName]) {
            groupMap[groupName] = { name: groupName, parameters: [] };
        }
    }

    const reportingGroup = groups.find(
        (group) => group.name === REPORTING_PARAMETER_GROUP_NAME || group.name === REPORTING_PARAMETER_GROUP_NEW_NAME,
    );

    if (reportingGroup) {
        for (const rawParam of reportingGroup.parameters) {
            const param = normaliseParameterMetadata(rawParam);
            const groupName: string = DEFAULT_SLUGS.includes(param.slug)
                ? getNewGroup(param.slug)
                : parameterTypeMap[param.parameter_type_id]?.name ?? 'Custom';

            if (!groupMap[groupName]) {
                groupMap[groupName] = { name: groupName, parameters: [] };
            }

            const existingGroup = groupMap[groupName];
            if (!existingGroup.parameters.some((existingParam) => existingParam.id === param.id)) {
                existingGroup.parameters.push(param);
            }
        }
    }

    const result: TfReportingParameterGroup[] = Object.values(groupMap).filter((group) => group.parameters.length > 0);

    result.push({
        name: 'Load Scores',
        parameters: [
            {
                aggregation: 'sum',
                calculation: '',
                created_at: '',
                ctr_order: -1,
                group_by: 'total',
                id: '-1',
                modified_at: '',
                name: 'Volume',
                original_name: 'Volume',
                parameter_type_id: '-1',
                slug: 'volume',
                unit_type: 'percentage',
            },
            {
                calculation: '',
                created_at: '',
                ctr_order: -2,
                id: '-2',
                modified_at: '',
                name: 'Intensity',
                original_name: 'Intensity',
                parameter_type_id: '-2',
                slug: 'intensity',
                unit_type: 'percentage',
            },
            {
                calculation: '',
                created_at: '',
                ctr_order: -3,
                id: '-3',
                modified_at: '',
                name: 'Overall',
                original_name: 'Overall',
                parameter_type_id: '-3',
                slug: 'overall',
                unit_type: 'percentage',
            },
        ],
    });

    return result;
};

export const getTranslation = (slug: string, isVectorCore: boolean): string => {
    if (REQUIRES_VECTOR_CORE_RENAME.includes(slug) && isVectorCore) {
        return `${VECTOR_CORE_TRANSLATION_PREFIX}${PARAMETER_NAME_TRANSLATION_PREFIX}${slug}`;
    }

    return `${PARAMETER_NAME_TRANSLATION_PREFIX}${slug}`;
};

export const transformParameterIntoGroups = (
    parameters: Parameter[],
    types: ParameterType[],
): TfReportingParameterGroup[] => {
    const normalised = parameters.map(normaliseParameterMetadata);
    const parameterGroups: TfReportingParameterGroup[] = types.map((type) => ({
        name: type.name ?? '',
        parameters: normalised.filter((param) => param.parameter_type_id === type.id),
    }));

    return parameterGroups.filter((group) => group.parameters.length > 0);
};

export const translateParameters = (
    parameters: TfReportingParameterGroup[],
    __: (key: string) => string,
    isVectorCore: boolean,
): TfReportingParameterGroup[] => {
    return parameters.map((group) => {
        const translatedParameters = group.parameters.map((param: TfParameter) => {
            return {
                ...param,
                // if the translation key is not found, its going to return in form 'parameter_names.<slug>', so use param name as fallback
                translated_name: __(getTranslation(param.slug, isVectorCore)).includes(
                    PARAMETER_NAME_TRANSLATION_PREFIX,
                )
                    ? param.name
                    : __(getTranslation(param.slug, isVectorCore)),
            };
        });

        return {
            ...group,
            name: __(group.name),
            parameters: translatedParameters,
        };
    });
};
